import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCard, Printer } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { apiClient, resolveUploadUrl } from '@/services/api'

interface IDCardData {
  student_id: string
  student_id_number: string
  full_name: string
  grade: number
  section?: string
  school_name: string
  photo_url?: string
  date_of_birth?: string
  qr_code_data: string
  emergency_contact?: string
}

interface StudentIDCardProps {
  studentId: string
  onClose?: () => void
}

export function StudentIDCard({ studentId, onClose }: StudentIDCardProps) {
  const { t } = useTranslation()
  const [data, setData] = useState<IDCardData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get(`/attendance/id-card/${studentId}`)
        setData(res.data ?? res)
      } catch (err) {
        console.error('[IDCard] Failed to load:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [studentId])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500" role="status" />
      </div>
    )
  }

  if (!data) {
    return <p className="text-center text-gray-400 py-8">{t('idCard.notFound', 'Student data not found')}</p>
  }

  const qrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(data.qr_code_data)}`

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={() => window.print()}>
          <Printer className="h-4 w-4 mr-1" />
          {t('common.print', 'Print')}
        </Button>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        )}
      </div>

      <div className="mx-auto max-w-sm print:max-w-full">
        <Card className="border-2 border-gray-200 dark:border-gray-600 overflow-hidden shadow-lg">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white p-4 text-center">
            <p className="text-lg font-bold tracking-wide">{data.school_name}</p>
            <p className="text-xs opacity-80 uppercase tracking-widest">{t('idCard.studentCard', 'Student Identity Card')}</p>
          </div>

          <CardContent className="p-5 space-y-4">
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0">
                {data.photo_url ? (
                  <img src={resolveUploadUrl(data.photo_url)} alt={data.full_name} className="w-24 h-28 object-cover rounded-lg border-2 border-gray-200" />
                ) : (
                  <div className="w-24 h-28 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                    <CreditCard className="h-8 w-8 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-1.5">
                <p className="text-lg font-bold text-gray-900 dark:text-white">{data.full_name}</p>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-0.5">
                  <p><span className="font-medium">{t('idCard.idNumber', 'ID')}:</span> {data.student_id_number}</p>
                  <p><span className="font-medium">{t('idCard.grade', 'Grade')}:</span> {data.grade}{data.section ? ` - ${data.section}` : ''}</p>
                  {data.date_of_birth && (
                    <p><span className="font-medium">{t('idCard.dob', 'DOB')}:</span> {new Date(data.date_of_birth).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-xs text-gray-400 space-y-0.5">
                {data.emergency_contact && (
                  <p><span className="font-medium">{t('idCard.emergency', 'Emergency')}:</span> {data.emergency_contact}</p>
                )}
                <p className="italic">{t('idCard.scanForVerification', 'Scan QR code for verification')}</p>
              </div>
              <img src={qrDataUrl} alt="QR Code" className="w-20 h-20 border rounded" />
            </div>
          </CardContent>

          <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 text-center">
            <p className="text-[10px] text-gray-400">{t('idCard.footer', 'This card is the property of the school. If found, please return.')}</p>
          </div>
        </Card>
      </div>
    </div>
  )
}
